import { useState, useRef, useEffect, ComponentPropsWithRef } from "react";
import SelectStyle from "./css/Select.module.css";
import clsx from "clsx";
import IcoDown from "../../assets/img/IcoDown";
import Text from "./Text";
import Button from "./Button";
import Flex from "./Flex";

type SelectOption = {
    text: string;
    value: string;
    default?: boolean;
}

type SelectProps = Omit<ComponentPropsWithRef<"input">, 'onChange' | 'type'> & {
    options: SelectOption[];
    onChange?: (value: string) => void;
    name: string;
    fullWidth?: boolean;
    dropdownBorderRadius?: number;
}

function Select({ 
    options, 
    onChange, 
    className,
    name,
    id,
    width,
    fullWidth = false,
    dropdownBorderRadius,
    disabled,
    ...props 
}: SelectProps) {
    const [isOpen, setIsOpen] = useState(false);
    const [focusedIndex, setFocusedIndex] = useState(-1);
    const selectRef = useRef<HTMLDivElement>(null);
    const buttonRef = useRef<HTMLButtonElement>(null);
    const optionRefs = useRef<(HTMLLIElement | null)[]>([]);
    const isMouseDownRef = useRef(false);
    
    // 초기값 설정: default가 true인 옵션, 없으면 첫 번째 옵션
    const getDefaultValue = () => {
        const defaultOption = options.find(opt => opt.default);
        return defaultOption ? defaultOption.value : (options[0]?.value || '');
    };
    
    const [selectedValue, setSelectedValue] = useState(getDefaultValue());

    // options이 변경되면 초기값 재설정
    useEffect(() => {
        setSelectedValue(getDefaultValue());
    }, [options]);

    // 드롭다운이 열릴 때 선택된 항목에 포커스
    useEffect(() => {
        if (isOpen) {
            const selectedIndex = options.findIndex(opt => opt.value === selectedValue);
            setFocusedIndex(selectedIndex >= 0 ? selectedIndex : 0);
            
            // 스크롤 위치 조정
            if (selectedIndex >= 0) {
                optionRefs.current[selectedIndex]?.scrollIntoView({ 
                    block: 'nearest' 
                });
            }
        }
    }, [isOpen, options, selectedValue]);

    // 포커스된 항목 자동 스크롤
    useEffect(() => {
        if (isOpen && focusedIndex >= 0) {
            optionRefs.current[focusedIndex]?.scrollIntoView({ 
                block: 'nearest',
                behavior: 'smooth'
            });
        }
    }, [focusedIndex, isOpen]);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (selectRef.current && !selectRef.current.contains(event.target as Node)) {
                setIsOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const handleSelect = (optionValue: string) => {
        setSelectedValue(optionValue);
        onChange?.(optionValue);
        setIsOpen(false);
    };

    const handleToggle = () => {
        if (!disabled) {
            setIsOpen(!isOpen);
        }
    };

    const handleMouseDown = () => {
        isMouseDownRef.current = true;
    };

    const handleFocus = () => {
        // 마우스 클릭으로 인한 포커스면 열지 않음 (onClick에서 처리)
        if (isMouseDownRef.current) {
            isMouseDownRef.current = false;
            return;
        }
        // 키보드 탭으로 인한 포커스일 때만 열기
        if (!disabled) {
            setIsOpen(true);
        }
    };

    // 키보드 이벤트 핸들러
    const handleKeyDown = (e: React.KeyboardEvent) => {
        // 드롭다운이 닫혀있을 때
        if (!isOpen) {
            switch (e.key) {
                case 'Enter':
                case ' ':
                case 'ArrowDown':
                case 'ArrowUp':
                    e.preventDefault();
                    setIsOpen(true);
                    break;
            }
            return;
        }

        // 드롭다운이 열려있을 때
        switch (e.key) {
            case 'ArrowDown':
                e.preventDefault();
                setFocusedIndex(prev => {
                    const next = prev + 1;
                    return next >= options.length ? 0 : next; // 순환
                });
                break;
                
            case 'ArrowUp':
                e.preventDefault();
                setFocusedIndex(prev => {
                    const next = prev - 1;
                    return next < 0 ? options.length - 1 : next; // 순환
                });
                break;
                
            case 'Enter':
            case ' ':
                e.preventDefault();
                if (focusedIndex >= 0) {
                    handleSelect(options[focusedIndex].value);
                }
                break;
                
            case 'Escape':
                e.preventDefault();
                setIsOpen(false);
                buttonRef.current?.focus();
                break;
                
            case 'Home':
                e.preventDefault();
                setFocusedIndex(0);
                break;
                
            case 'End':
                e.preventDefault();
                setFocusedIndex(options.length - 1);
                break;
                
            case 'Tab':
                // Tab은 기본 동작 유지 (다음 요소로 이동)
                setIsOpen(false);
                break;
        }
    };

    // 현재 선택된 옵션 찾기
    const selectedOption = options.find(opt => opt.value === selectedValue);

    return (
        <div 
            className={clsx(SelectStyle.select, className)} 
            ref={selectRef}
            role="combobox"
            aria-expanded={isOpen}
            aria-haspopup="listbox"
            aria-label={props['aria-label'] || name}
            style={{
                width: fullWidth ? "100%" : width,
            }}
            {...(isOpen ? { "aria-controls": "select-listbox" } : {})}
        >
            <input
                type="hidden"
                name={name||""}
                value={selectedValue}
            />

            <Button
                ref={buttonRef}
                variant="white"
                fullWidth
                onMouseDown={handleMouseDown}
                onClick={handleToggle}
                onFocus={handleFocus}
                onKeyDown={handleKeyDown}
                disabled={disabled}
                type="button"
                aria-haspopup="listbox"
                aria-expanded={isOpen}
                id={id}
                style={{
                    ...props.style,
                    padding: "13px 15px 13px 20px",
                    borderRadius: "8px",
                    border: "1px solid rgb(221, 221, 221)",
                    justifyContent: "space-between",
                    alignItems: "center",
                    display: "flex",
                }}
            >
                    <Text variant="span" size={16}>
                        {selectedOption?.text || options[0]?.text || ''}
                    </Text>
                    <span className={
                        clsx(SelectStyle.arrow, isOpen && SelectStyle.open)
                    }>
                        <IcoDown/>
                    </span>
            </Button>

            {isOpen && !disabled && (
                <ul 
                    id="select-listbox"
                    className={SelectStyle.dropdown} 
                    role="listbox"
                    aria-label={`${name} options`}
                    style={{
                        borderRadius: dropdownBorderRadius ? `${dropdownBorderRadius}px` : undefined,
                        border: "1px solid rgb(221, 221, 221)",
                    }}
                >
                    {options.map((option, index) => (
                        <li
                            ref={el => { optionRefs.current[index] = el; }}
                            key={option.value}
                            role="option"
                            aria-selected={selectedValue === option.value}
                            data-focused={focusedIndex === index}
                            className={clsx(
                                SelectStyle.option,
                                selectedValue === option.value && SelectStyle.selected,
                                focusedIndex === index && SelectStyle.focused
                            )}
                            onClick={() => handleSelect(option.value)}
                            onMouseEnter={() => setFocusedIndex(index)}
                        >
                            <Text>{option.text}</Text>
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
}

export default Select;